import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, primaryImage, titleize, formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Building2, Mail, Users2, LayoutDashboard, CheckCircle2, XCircle, Sparkles, BadgeCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";

export function AdminLayout() {
    const { user, loading } = useAuth();
    const nav = useNavigate();
    useEffect(() => {
        if (!loading && (!user || user.role !== "admin")) nav("/");
    }, [loading, user, nav]);

    if (loading || !user || user.role !== "admin") return <div className="container-app py-24 text-center text-muted-foreground">Loading…</div>;

    const link = ({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`;
    return (
        <div className="container-app py-8 grid md:grid-cols-[240px_1fr] gap-8">
            <aside className="md:sticky md:top-24 md:self-start">
                <div className="rounded-2xl border border-border bg-card p-3">
                    <p className="px-2 py-1 text-xs uppercase tracking-widest text-muted-foreground">Admin</p>
                    <nav className="mt-1 flex flex-col gap-1">
                        <NavLink to="/admin" end className={link}><LayoutDashboard className="h-4 w-4" /> Overview</NavLink>
                        <NavLink to="/admin/venues" className={link} data-testid="admin-nav-venues"><Building2 className="h-4 w-4" /> Venues</NavLink>
                        <NavLink to="/admin/enquiries" className={link}><Mail className="h-4 w-4" /> Enquiries</NavLink>
                        <NavLink to="/admin/users" className={link}><Users2 className="h-4 w-4" /> Users</NavLink>
                        <NavLink to="/admin/import" className={link} data-testid="admin-nav-import"><Upload className="h-4 w-4" /> Import CSV</NavLink>
                    </nav>
                </div>
            </aside>
            <div><Outlet /></div>
        </div>
    );
}

export function AdminOverview() {
    const [stats, setStats] = useState(null);
    useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
    const cards = stats ? [
        { label: "Total venues", value: stats.venues_total },
        { label: "Pending review", value: stats.venues_pending },
        { label: "Published", value: stats.venues_published },
        { label: "Enquiries", value: stats.enquiries_total },
        { label: "Users", value: stats.users_total },
        { label: "SEO pages", value: stats.seo_pages },
    ] : [];

    return (
        <div>
            <h1 className="font-display text-3xl font-semibold" data-testid="admin-overview-h1">Admin overview</h1>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
                {cards.map((c) => (
                    <div key={c.label} className="rounded-2xl border border-border bg-card p-6">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">{c.label}</p>
                        <p className="font-display text-3xl mt-1 font-semibold">{c.value}</p>
                    </div>
                ))}
            </div>
            <div className="mt-8">
                <Button asChild className="rounded-full bg-secondary hover:bg-secondary/90"><Link to="/admin/venues?status=pending">Review pending venues →</Link></Button>
            </div>
        </div>
    );
}

export function AdminVenues() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");

    const load = () => {
        const q = filter === "all" ? "" : `?status=${filter}`;
        api.get(`/admin/venues${q}`).then((r) => setItems(r.data.items || []));
    };
    useEffect(load, [filter]);

    const act = async (id, action) => {
        await api.post(`/admin/venues/${id}/action`, { action });
        toast.success(`Venue ${action}d`);
        load();
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6 gap-4">
                <h1 className="font-display text-3xl font-semibold">All venues</h1>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="admin-venue-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Venue</TableHead>
                            <TableHead>City</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Badges</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((v) => (
                            <TableRow key={v.venue_id} data-testid={`admin-venue-row-${v.venue_id}`}>
                                <TableCell>
                                    <div className="flex items-center gap-3">
                                        <img src={primaryImage(v)} className="h-10 w-14 rounded object-cover" alt="" />
                                        <div><p className="font-medium">{v.name}</p><p className="text-xs text-muted-foreground">{titleize(v.venue_type)}</p></div>
                                    </div>
                                </TableCell>
                                <TableCell>{v.city}, {v.country}</TableCell>
                                <TableCell><Badge variant="outline">{v.status}</Badge></TableCell>
                                <TableCell>
                                    <div className="flex gap-1">
                                        {v.is_featured && <Badge className="bg-secondary text-secondary-foreground gap-1"><Sparkles className="h-3 w-3" />F</Badge>}
                                        {v.is_verified && <Badge className="bg-primary text-primary-foreground gap-1"><BadgeCheck className="h-3 w-3" />V</Badge>}
                                    </div>
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex gap-1 justify-end flex-wrap">
                                        {v.status !== "published" && <Button size="sm" variant="outline" onClick={() => act(v.venue_id, "approve")} data-testid={`approve-${v.venue_id}`}><CheckCircle2 className="h-3 w-3 mr-1" />Approve</Button>}
                                        {v.status === "published" && <Button size="sm" variant="ghost" onClick={() => act(v.venue_id, "suspend")}><XCircle className="h-3 w-3 mr-1" />Suspend</Button>}
                                        <Button size="sm" variant={v.is_featured ? "default" : "outline"} onClick={() => act(v.venue_id, v.is_featured ? "unfeature" : "feature")}>{v.is_featured ? "Unfeature" : "Feature"}</Button>
                                        <Button size="sm" variant={v.is_verified ? "default" : "outline"} onClick={() => act(v.venue_id, v.is_verified ? "unverify" : "verify")}>{v.is_verified ? "Unverify" : "Verify"}</Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {items.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No venues match.</div>}
            </div>
        </div>
    );
}

export function AdminEnquiries() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/admin/enquiries").then((r) => setItems(r.data.items || [])); }, []);
    return (
        <div>
            <h1 className="font-display text-3xl font-semibold mb-6">All enquiries</h1>
            <div className="space-y-3">
                {items.map((e) => (
                    <div key={e.enquiry_id} className="rounded-xl border border-border bg-card p-4">
                        <div className="flex justify-between items-start flex-wrap gap-2">
                            <div>
                                <p className="font-semibold">{e.name} → {e.venue_name}</p>
                                <p className="text-xs text-muted-foreground">{e.email} {e.phone ? `• ${e.phone}` : ""}</p>
                            </div>
                            <Badge variant="outline">{e.status}</Badge>
                        </div>
                        <p className="mt-2 text-sm">{e.message}</p>
                        <p className="text-xs text-muted-foreground mt-2">{e.event_type ? titleize(e.event_type) : "-"} • {e.event_date || "Date TBC"} • {e.guests || "?"} guests</p>
                    </div>
                ))}
                {items.length === 0 && <div className="rounded-xl border border-dashed p-12 text-center text-muted-foreground">No enquiries yet.</div>}
            </div>
        </div>
    );
}

export function AdminUsers() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/admin/users").then((r) => setItems(r.data.items || []));
    useEffect(() => { load(); }, []);
    const setRole = async (uid, role) => { await api.post(`/admin/users/${uid}/role`, { role }); toast.success("Role updated"); load(); };
    return (
        <div>
            <h1 className="font-display text-3xl font-semibold mb-6">Users</h1>
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <Table>
                    <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead></TableRow></TableHeader>
                    <TableBody>
                        {items.map((u) => (
                            <TableRow key={u.user_id}>
                                <TableCell>{u.name || "-"}</TableCell>
                                <TableCell>{u.email}</TableCell>
                                <TableCell>
                                    <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)}>
                                        <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="seeker">Seeker</SelectItem>
                                            <SelectItem value="owner">Owner</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
